import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import {
  LogOut, Package, CheckCircle, AlertCircle,
  ExternalLink, Languages, Truck, Send, Pause,
  ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';

// ── Translations ──────────────────────────────────────────────────────────────
const cd = {
  es: {
    greeting: 'Hola,',
    logout: 'Cerrar sesión',
    casilleroTitle: 'Mis Paquetes',
    encomiendaTitle: 'Mis Encomiendas',
    noItems: 'No tiene paquetes ni encomiendas registradas aún.',
    loading: 'Cargando...',
    tracking: 'Tracking',
    encStatus: 'COMPLETADO',
    receiptPending: 'Recibo pendiente',
    receiptUploaded: 'Recibo adjunto',
    uploadReceipt: '📎 Adjuntar recibo(s)',
    uploadReceiptPkg: '📎 Subir Recibo',
    viewReceipt: 'Ver Recibo',
    replace: 'Reemplazar',
    fileTooLarge: 'Archivo muy grande. Máximo 5MB.',
    uploading: 'Subiendo...',
    pickUp: 'PICK UP',
    dropOff: 'DROP OFF',
    shipNow: 'ENVÍO',
    hold: 'EN ESPERA',
    dest: 'destinatario',
    dests: 'destinatarios',
    receiptsPending: 'recibo(s) pendiente(s)',
    successAttached: '✓ Recibo adjuntado exitosamente',
    addMore: 'Agregar recibo',
    viewAll: 'Ver recibos',
    deleteReceipt: 'Eliminar',
    confirmDelete: '¿Eliminar este recibo?',
    deleteFailed: 'Error al eliminar el recibo. Intente de nuevo.',
    staffUploaded: 'Subido por personal',
    capReached: 'Máximo 20 recibos por paquete.',
    noReceipts: 'Sin recibos adjuntos',
    closeViewer: 'Cerrar',
    receiptCount: 'recibo(s)',
  },
  en: {
    greeting: 'Hello,',
    logout: 'Log out',
    casilleroTitle: 'My Packages',
    encomiendaTitle: 'My Encomiendas',
    noItems: 'No packages or encomiendas registered yet.',
    loading: 'Loading...',
    tracking: 'Tracking',
    encStatus: 'COMPLETE',
    receiptPending: 'Receipt pending',
    receiptUploaded: 'Receipt attached',
    uploadReceipt: '📎 Attach receipt(s)',
    uploadReceiptPkg: '📎 Upload Receipt',
    viewReceipt: 'View Receipt',
    replace: 'Replace',
    fileTooLarge: 'File too large. Max 5MB.',
    uploading: 'Uploading...',
    pickUp: 'PICK UP',
    dropOff: 'DROP OFF',
    shipNow: 'SHIP',
    hold: 'HOLD',
    dest: 'recipient',
    dests: 'recipients',
    receiptsPending: 'receipt(s) pending',
    successAttached: '✓ Receipt attached successfully',
    addMore: 'Add receipt',
    viewAll: 'View receipts',
    deleteReceipt: 'Delete',
    confirmDelete: 'Delete this receipt?',
    deleteFailed: 'Could not delete receipt. Try again.',
    staffUploaded: 'Uploaded by staff',
    capReached: 'Maximum 20 receipts per package.',
    noReceipts: 'No receipts attached',
    closeViewer: 'Close',
    receiptCount: 'receipt(s)',
  },
};

// ── Receipt Viewer Modal (carousel for multi-receipt view) ────────────────────
const ReceiptViewerModal = ({ receipts, wt, onClose, onDelete }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const touchStartX = useRef(0);
  const list = receipts || [];

  if (list.length === 0) return null;
  const safeIdx = Math.min(currentIdx, list.length - 1);
  const current = list[safeIdx];

  const isImage = (url) => {
    const lower = (url || '').toLowerCase();
    return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.webp');
  };

  const handleDelete = async () => {
    if (!current) return;
    if (!window.confirm(wt.confirmDelete)) return;
    try {
      await onDelete(current);
      if (safeIdx >= list.length - 1) {
        setCurrentIdx(Math.max(0, list.length - 2));
      }
    } catch (err) {
      alert(wt.deleteFailed);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        zIndex: 9999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 40) {
            if (diff > 0) setCurrentIdx(i => Math.min(list.length - 1, i + 1));
            else setCurrentIdx(i => Math.max(0, i - 1));
          }
        }}
        style={{
          background: 'var(--tile)', borderRadius: '12px', padding: '16px',
          maxWidth: '600px', width: '100%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', gap: '12px',
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">
            {safeIdx + 1} / {list.length}
          </span>
          <button
            onClick={onClose}
            className="btn btn-secondary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
          >
            {wt.closeViewer}
          </button>
        </div>

        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#f0f0f0', borderRadius: '8px', minHeight: '300px',
          maxHeight: '60vh', overflow: 'hidden',
        }}>
          {isImage(current.receipt_url) ? (
            <img
              src={current.receipt_url}
              alt={`Receipt ${safeIdx + 1}`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <a
              href={current.receipt_url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
              style={{ padding: '12px 16px' }}
            >
              <ExternalLink size={14} className="mr-2" />
              {wt.viewReceipt}
            </a>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
            disabled={safeIdx === 0}
            className="btn btn-secondary"
            style={{ padding: '6px 16px', opacity: safeIdx === 0 ? 0.4 : 1 }}
          >
            ←
          </button>

          <div className="flex items-center gap-2">
            {current.source === 'staff' ? (
              <span className="text-[10px] text-gray-500 italic">{wt.staffUploaded}</span>
            ) : (
              <button
                onClick={handleDelete}
                className="text-[11px] text-red-600 hover:text-red-800 font-medium"
                style={{ padding: '4px 8px' }}
              >
                {wt.deleteReceipt}
              </button>
            )}
          </div>

          <button
            onClick={() => setCurrentIdx(i => Math.min(list.length - 1, i + 1))}
            disabled={safeIdx === list.length - 1}
            className="btn btn-secondary"
            style={{ padding: '6px 16px', opacity: safeIdx === list.length - 1 ? 0.4 : 1 }}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Package photo fullscreen overlay ─────────────────────────────────────────
const PhotoOverlay = ({ url, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
      zIndex: 9999, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '16px',
    }}
  >
    <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '600px', width: '100%' }}>
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '-36px', right: 0,
          background: 'transparent', border: 'none',
          color: '#fff', fontSize: '13px', cursor: 'pointer', padding: '4px 8px',
        }}
      >
        ✕ Cerrar
      </button>
      <img
        src={url}
        alt="Foto del paquete"
        style={{ width: '100%', borderRadius: '10px', objectFit: 'contain', maxHeight: '80vh' }}
      />
    </div>
  </div>
);

const PackageGridCard = ({ s, isShip, photoUrl, wt, onUploaded }) => {
  const [showPhoto, setShowPhoto] = useState(false);
  return (
    <div style={{
      background: 'var(--tile)', border: '0.5px solid var(--border)',
      borderRadius: '12px', padding: '10px',
      display: 'flex', flexDirection: 'column', gap: '7px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '4px' }}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: 'var(--blue)', margin: 0 }}>
          {s.unique_package_id}
        </p>
        <span className={`badge ${isShip ? 'badge-done' : 'badge-hold'}`} style={{ fontSize: '9px', padding: '2px 6px', flexShrink: 0 }}>
          {isShip ? <Send size={9} /> : <Pause size={9} />}
          {isShip ? wt.shipNow : wt.hold}
        </span>
      </div>

      {/* Info */}
      <div style={{ minWidth: 0 }}>
        {s.recipient_name && (
          <p style={{ fontSize: '10px', color: 'var(--text-secondary)', margin: '0 0 1px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.recipient_name}
          </p>
        )}
        {s.tracking_number ? (
          <p style={{ fontSize: '9px', fontFamily: 'monospace', color: 'var(--text-secondary)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.tracking_number}
          </p>
        ) : (
          <p style={{ fontSize: '9px', color: 'var(--border)', margin: '0 0 2px', fontStyle: 'italic' }}>Sin tracking</p>
        )}
        <p style={{ fontSize: '9px', color: 'var(--text-secondary)', margin: 0 }}>
          {s.entry_timestamp ? new Date(s.entry_timestamp).toLocaleDateString() : '---'}
        </p>
      </div>

      {/* Package photo */}
      <div
        onClick={() => photoUrl && setShowPhoto(true)}
        style={{
          width: '100%', aspectRatio: '3/2', borderRadius: '6px',
          overflow: 'hidden', border: '0.5px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: photoUrl ? 'pointer' : 'default',
          background: 'var(--bg)',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt="Foto del paquete"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <Package size={20} style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <span style={{ fontSize: '9px', color: 'var(--text-secondary)', opacity: 0.6 }}>Foto pendiente</span>
          </div>
        )}
      </div>

      {/* Receipt section */}
      <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: '7px' }}>
        <CasilleroReceiptButton shipment={s} wt={wt} onUploaded={onUploaded} />
      </div>

      {showPhoto && photoUrl && <PhotoOverlay url={photoUrl} onClose={() => setShowPhoto(false)} />}
    </div>
  );
};

// ── Casillero Receipt Button (multi-upload + carousel + delete) ───────────────
const CasilleroReceiptButton = ({ shipment, wt, onUploaded }) => {
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showViewer, setShowViewer] = useState(false);

  const fetchReceipts = async () => {
    const { data } = await supabase
      .from('package_receipts')
      .select('*')
      .eq('shipment_id', shipment.id)
      .order('uploaded_at', { ascending: true });
    setReceipts(data || []);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { fetchReceipts(); }, [shipment.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 20 - (receipts || []).length;
    if (remaining <= 0) {
      setError(wt.capReached);
      e.target.value = '';
      return;
    }

    const toUpload = files.slice(0, remaining);
    setError('');
    setUploading(true);

    let lastUrl = null;
    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) { setError(wt.fileTooLarge); continue; }
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const filePath = `receipts/${shipment.manifest_id}/${shipment.customer_id}-${shipment.unique_package_id}-${Date.now()}.${ext}`;
        await supabase.storage.from('package-photos').upload(filePath, file);
        const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(filePath);
        await supabase.from('package_receipts').insert({
          shipment_id: shipment.id,
          receipt_url: urlData.publicUrl,
          uploaded_by: 'client',
          source: 'client',
          manifest_id: shipment.manifest_id,
        });
        lastUrl = urlData.publicUrl;
      } catch (err) {
        setError(err.message);
      }
    }

    // Legacy: keep shipments.receipt_url pointing at the most recent client upload
    if (lastUrl) {
      try {
        await supabase.from('shipments').update({ receipt_url: lastUrl }).eq('id', shipment.id);
      } catch (legacyErr) {
        console.warn('[CasilleroReceipt] legacy receipt_url update failed (non-blocking):', legacyErr);
      }
    }

    await fetchReceipts();
    setSuccessMsg(wt.successAttached);
    setTimeout(() => setSuccessMsg(''), 3000);
    setUploading(false);
    e.target.value = '';
    if (onUploaded) onUploaded();
  };

  const handleDelete = async (receipt) => {
    if (!receipt) return;

    // Storage cleanup — non-blocking
    try {
      const url = receipt.receipt_url || '';
      const marker = '/package-photos/';
      const idx = url.indexOf(marker);
      if (idx > -1) {
        const path = url.slice(idx + marker.length);
        await supabase.storage.from('package-photos').remove([path]);
      }
    } catch (storageErr) {
      console.warn('[CasilleroReceipt] Storage cleanup failed (non-blocking):', storageErr);
    }

    const { error: dbErr } = await supabase
      .from('package_receipts')
      .delete()
      .eq('id', receipt.id);

    if (dbErr) throw dbErr;
    await fetchReceipts();
  };

  const count = (receipts || []).length;
  const hasReceipts = count > 0;

  return (
    <div style={{ marginTop: '4px' }}>
      {successMsg && (
        <p style={{ fontSize: '10px', color: 'var(--green, #3B6D11)', fontWeight: '500', marginBottom: '6px' }}>
          {successMsg}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {hasReceipts ? (
          <>
            {/* Most recent receipt thumbnail — clicking opens carousel */}
            <button
              type="button"
              onClick={() => setShowViewer(true)}
              style={{
                width: '48px', height: '48px', borderRadius: '8px',
                border: '0.5px solid var(--blue, #185FA5)',
                background: 'var(--blue-soft, #E6F1FB)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', padding: 0,
                position: 'relative', flexShrink: 0,
              }}
              title={wt.viewAll}
            >
              {receipts[receipts.length - 1]?.receipt_url?.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                <img
                  src={receipts[receipts.length - 1].receipt_url}
                  alt="recibo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '7px' }}
                />
              ) : (
                <ExternalLink size={16} style={{ color: 'var(--blue-text, #185FA5)' }} />
              )}
              {count > 1 && (
                <span style={{
                  position: 'absolute', bottom: '2px', right: '3px',
                  fontSize: '8px', fontWeight: '600',
                  color: 'var(--blue-text, #185FA5)',
                  background: 'rgba(230,241,251,0.92)',
                  borderRadius: '3px', padding: '0 2px', lineHeight: '1.4',
                }}>
                  {count}
                </span>
              )}
            </button>

            {/* Add more — dashed square */}
            {count < 20 && (
              <label style={{
                width: '48px', height: '48px', borderRadius: '8px',
                border: '1px dashed var(--border, #ccc)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', flexShrink: 0, gap: '2px',
              }}>
                <span style={{ fontSize: '18px', color: 'var(--text-secondary)', lineHeight: 1 }}>+</span>
                <span style={{ fontSize: '8px', color: 'var(--text-secondary)' }}>
                  {uploading ? '...' : wt.addMore}
                </span>
                <input
                  type="file" className="hidden" multiple
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFiles} disabled={uploading}
                />
              </label>
            )}

            {/* Count label */}
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {count} {wt.receiptCount}
            </span>
          </>
        ) : (
          /* No receipts yet — full upload button */
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
            border: '1px solid var(--amber, #d97706)',
            background: 'var(--amber-soft, #FEF3C7)',
            fontSize: '11px', fontWeight: '600',
            color: 'var(--amber-text, #92400e)',
          }}>
            <span style={{ fontSize: '13px' }}>📎</span>
            {uploading ? wt.uploading : wt.uploadReceipt}
            <input
              type="file" className="hidden" multiple
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleFiles} disabled={uploading}
            />
          </label>
        )}
      </div>

      {error && <p style={{ fontSize: '10px', color: 'var(--avianca-red, #dc2626)', marginTop: '4px' }}>{error}</p>}

      {showViewer && (
        <ReceiptViewerModal
          receipts={receipts}
          wt={wt}
          onClose={() => setShowViewer(false)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

// ── Encomienda Sub-package Receipt Button (multi-upload + carousel + delete) ──
const EncReceiptButton = ({ pkg, encPublicId, wt, onUploaded }) => {
  const [receipts, setReceipts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showViewer, setShowViewer] = useState(false);

  const fetchReceipts = async () => {
    const { data } = await supabase
      .from('package_receipts')
      .select('*')
      .eq('encomienda_package_id', pkg.id)
      .order('uploaded_at', { ascending: true });
    setReceipts(data || []);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { fetchReceipts(); }, [pkg.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 20 - (receipts || []).length;
    if (remaining <= 0) {
      setError(wt.capReached);
      e.target.value = '';
      return;
    }

    const toUpload = files.slice(0, remaining);
    setError('');
    setUploading(true);

    let lastUrl = null;
    for (const file of toUpload) {
      if (file.size > 5 * 1024 * 1024) { setError(wt.fileTooLarge); continue; }
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const fileName = `${encPublicId}-PK${pkg.package_number}-${Date.now()}.${ext}`;
        await supabase.storage.from('package-photos').upload(fileName, file);
        const { data: urlData } = supabase.storage.from('package-photos').getPublicUrl(fileName);
        await supabase.from('package_receipts').insert({
          encomienda_package_id: pkg.id,
          receipt_url: urlData.publicUrl,
          uploaded_by: 'client',
          source: 'client',
        });
        lastUrl = urlData.publicUrl;
      } catch (err) {
        setError(err.message);
      }
    }

    // Legacy: keep encomienda_packages.receipt_url pointing at most recent
    if (lastUrl) {
      try {
        await supabase.from('encomienda_packages').update({ receipt_url: lastUrl }).eq('id', pkg.id);
      } catch (legacyErr) {
        console.warn('[EncReceipt] legacy receipt_url update failed (non-blocking):', legacyErr);
      }
    }

    await fetchReceipts();
    setUploading(false);
    e.target.value = '';
    if (onUploaded) onUploaded();
  };

  const handleDelete = async (receipt) => {
    if (!receipt) return;

    try {
      const url = receipt.receipt_url || '';
      const marker = '/package-photos/';
      const idx = url.indexOf(marker);
      if (idx > -1) {
        const path = url.slice(idx + marker.length);
        await supabase.storage.from('package-photos').remove([path]);
      }
    } catch (storageErr) {
      console.warn('[EncReceipt] Storage cleanup failed (non-blocking):', storageErr);
    }

    const { error: dbErr } = await supabase
      .from('package_receipts')
      .delete()
      .eq('id', receipt.id);

    if (dbErr) throw dbErr;
    await fetchReceipts();
  };

  const count = (receipts || []).length;
  const hasReceipts = count > 0;

  return (
    <div className="mt-1">
      {hasReceipts ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1 text-green-600 text-[10px] font-bold">
            <CheckCircle size={11} /> {count} {wt.receiptCount}
          </span>
          <button
            type="button"
            onClick={() => setShowViewer(true)}
            className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 font-medium"
            style={{ padding: 0, background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <ExternalLink size={10} /> {wt.viewAll}
          </button>
          {count < 20 && (
            <label className="text-[10px] text-orange-500 hover:text-orange-700 cursor-pointer font-medium">
              + {wt.addMore}
              <input
                type="file"
                className="hidden"
                multiple
                accept="image/jpeg,image/png,application/pdf"
                onChange={handleFiles}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      ) : (
        <label className={`cursor-pointer inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold transition border ${
          uploading ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200'
        }`}>
          {uploading ? wt.uploading : wt.uploadReceiptPkg}
          <input
            type="file"
            className="hidden"
            multiple
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFiles}
            disabled={uploading}
          />
        </label>
      )}

      {error && <p className="text-red-500 text-[10px] mt-0.5">{error}</p>}

      {showViewer && (
        <ReceiptViewerModal
          receipts={receipts}
          wt={wt}
          onClose={() => setShowViewer(false)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

// ── Expandable Encomienda Card ────────────────────────────────────────────────
const EncomiendaCard = ({ enc, wt, onUploaded }) => {
  const [expanded, setExpanded] = useState(false);
  const pkgs = enc.encomienda_packages || [];
  const missingReceipts = pkgs.filter(p => !p.receipt_url).length;

  return (
    <div className="tile" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header — clickable to expand */}
      <div
        className="enc-card-header p-4 flex items-center justify-between gap-3 flex-wrap cursor-pointer hover:bg-gray-50 transition select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="min-w-0">
          <p className="row-id" style={{ width: 'auto', fontSize: '16px', color: 'var(--avianca-red)' }}>{enc.unique_encomienda_id}</p>
          <p className="page-subtitle mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{pkgs.length} {pkgs.length === 1 ? wt.dest : wt.dests}</span>
            {missingReceipts > 0 && (
              <span className="badge badge-error flex items-center gap-1">
                <AlertCircle size={10} />
                {missingReceipts} {wt.receiptsPending}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="badge badge-done">
            {wt.encStatus}
          </span>
          {expanded
            ? <ChevronUp size={14} className="text-gray-400" />
            : <ChevronDown size={14} className="text-gray-400" />
          }
        </div>
      </div>

      {/* Expanded: per-package rows */}
      {expanded && (
        <div className="divide-y divide-gray-50 border-t border-gray-100 px-4" style={{ borderColor: 'var(--border)' }}>
          {pkgs.length === 0 ? (
            <p className="empty-state">Sin paquetes registrados.</p>
          ) : (
            pkgs.map(pkg => (
              <div key={pkg.id} className="py-3 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="row-id" style={{ width: 'auto', fontSize: '12px', color: 'var(--avianca-red)' }}>PK-{pkg.package_number}</span>
                  <span className="text-sm font-medium truncate flex-1" style={{ color: 'var(--text-main)' }}>{pkg.recipient_name}</span>
                  <span className={`badge ${
                    pkg.delivery_type === 'Pick Up'
                      ? 'badge-done'
                      : 'badge-ship'
                  }`}>
                    {pkg.delivery_type === 'Pick Up' ? wt.pickUp : wt.dropOff}
                  </span>
                  {pkg.total_fob != null && (
                    <span className="text-xs font-mono font-bold shrink-0" style={{ color: 'var(--green)' }}>
                      ${parseFloat(pkg.total_fob).toFixed(2)} FOB
                    </span>
                  )}
                </div>
                <EncReceiptButton
                  pkg={pkg}
                  encPublicId={enc.unique_encomienda_id}
                  wt={wt}
                  onUploaded={onUploaded}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── ClientDashboard ───────────────────────────────────────────────────────────
const ClientDashboard = ({ customer, onLogout }) => {
  const [lang, setLang] = useState('es');
  const wt = cd[lang];

  const [activePackages, setActivePackages] = useState([]);
  const [closedPackages, setClosedPackages] = useState([]);
  const [showClosedHistory, setShowClosedHistory] = useState(false);
  const [encomiendas, setEncomiendas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const { data: shipData, error: shipError } = await supabase
        .from('shipments')
        .select('*')
        .eq('customer_id', customer.id)
        .order('updated_at', { ascending: false });

      if (shipError) {
        console.error('[ClientDashboard]', shipError);
      } else {
        console.log('[ClientDashboard] total:', shipData?.length);

        // Use manifest_status mirror column — drift was fixed in backfill_shipments_manifest_status_drift
        // migration and is kept in sync by the manifest close handler. Avoids manifests!inner join
        // which requires RLS-passthrough on manifests for the anon client portal role.
        const active = (shipData || []).filter(p => (p.manifest_status || '') === 'OPEN');
        const closed = (shipData || []).filter(p => (p.manifest_status || '') === 'CLOSED');

        console.log('[ClientDashboard] active:', active.length);
        console.log('[ClientDashboard] closed:', closed.length);

        setActivePackages(active);
        setClosedPackages(closed);
      }

      const { data: encData } = await supabase
        .from('encomiendas')
        .select(`
          *,
          encomienda_packages (
            id,
            recipient_name,
            package_number,
            delivery_type,
            receipt_url,
            total_fob
          )
        `)
        .eq('customer_id', customer.id)
        .eq('status', 'COMPLETE')
        .order('created_at', { ascending: false });

      setEncomiendas(encData || []);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!customer?.id) return;

    fetchClientData();

    // Realtime: re-fetch when this client's shipments change
    const channel = supabase
      .channel(`client-dashboard-${customer.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shipments', filter: `customer_id=eq.${customer.id}` },
        () => { fetchClientData(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'encomiendas', filter: `customer_id=eq.${customer.id}` },
        () => { fetchClientData(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'package_receipts' },
        () => { fetchClientData(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [customer?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const hasAnything = activePackages.length > 0 || closedPackages.length > 0 || encomiendas.length > 0;

  const groupByManifest = (packages) => {
    const groups = {};
    packages.forEach(pkg => {
      const key = pkg.manifest_id || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          manifestId: key,
          manifestName: key,
          closedAt: null,
          packages: [],
        };
      }
      groups[key].packages.push(pkg);
    });
    return Object.values(groups).sort((a, b) =>
      a.manifestId < b.manifestId ? 1 : -1
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header — no admin controls */}
      <nav className="top-nav">
        <span className="logo">
          comando <em>ops</em>
        </span>
        <div className="nav-right">
          <button
            onClick={() => fetchClientData()}
            className="btn btn-ghost"
            style={{ fontSize: '10px', color: 'var(--text-secondary)' }}
            title="Actualizar"
          >
            <RefreshCw size={13} />
          </button>
          <button
            onClick={() => setLang(l => l === 'es' ? 'en' : 'es')}
            className="btn btn-secondary"
            style={{ fontSize: '10px' }}
          >
            <Languages size={13} /> {lang === 'es' ? 'EN' : 'ES'}
          </button>
          <button
            onClick={onLogout}
            className="btn btn-ghost"
          >
            <LogOut size={15} /> {wt.logout}
          </button>
        </div>
      </nav>

      <main className="page-container" style={{ maxWidth: '800px' }}>
        {/* Greeting */}
        <div className="page-header">
          <div>
            <h1 className="page-title">
              {wt.greeting} <span style={{ color: 'var(--blue-text)' }}>{customer.full_name}</span>
            </h1>
            <p className="page-subtitle">ID: {customer.id}</p>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">{wt.loading}</div>
        ) : !hasAnything ? (
          <div className="empty-state">
            <Package size={40} className="mx-auto mb-3 opacity-30" />
            <p>{wt.noItems}</p>
          </div>
        ) : (
          <>
            {/* ── SECTION A: Casillero shipments ── */}
            {(activePackages.length > 0 || closedPackages.length > 0) && (
              <section className="space-y-3">
                <h2 className="metric-label flex items-center gap-2">
                  <Package size={13} /> {wt.casilleroTitle}
                </h2>

                {/* Active packages */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                      Paquetes activos
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      padding: '1px 7px',
                      borderRadius: '10px',
                      background: 'var(--color-background-info)',
                      color: 'var(--color-text-info)',
                    }}>
                      {activePackages.length} paquetes
                    </span>
                  </div>

                  {activePackages.length === 0 ? (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--color-text-tertiary)',
                      fontStyle: 'italic',
                      padding: '16px 0',
                    }}>
                      No tienes paquetes activos en este momento.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                      {activePackages.map(s => {
                        const isShip = s.shipping_status === 'Send Immediately';
                        const photoUrl = s.photo_url || null;
                        return (
                          <PackageGridCard
                            key={s.id}
                            s={s}
                            isShip={isShip}
                            photoUrl={photoUrl}
                            wt={wt}
                            onUploaded={fetchClientData}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Closed packages history */}
                {closedPackages.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-tertiary)' }} />
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '500',
                        color: 'var(--color-text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                      }}>
                        Manifiestos cerrados
                      </span>
                      <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-tertiary)' }} />
                    </div>

                    <div style={{
                      background: 'var(--color-background-primary)',
                      border: '0.5px solid var(--color-border-tertiary)',
                      borderRadius: 'var(--border-radius-lg)',
                      overflow: 'hidden',
                    }}>
                      <div
                        onClick={() => setShowClosedHistory(prev => !prev)}
                        style={{
                          padding: '12px 14px',
                          minHeight: '48px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                          Paquetes cerrados
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            background: 'var(--green-soft)',
                            color: 'var(--green)',
                            marginLeft: '6px',
                          }}>
                            {closedPackages.length} enviados
                          </span>
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {showClosedHistory
                            ? <ChevronUp size={18} style={{ color: 'var(--text-tertiary)' }} />
                            : <ChevronDown size={18} style={{ color: 'var(--text-tertiary)' }} />
                          }
                        </div>
                      </div>

                      {showClosedHistory && (
                        <div style={{ borderTop: '0.5px solid var(--color-border-tertiary)', padding: '12px 14px' }}>
                          {groupByManifest(closedPackages).map(group => (
                            <div key={group.manifestId} style={{ marginBottom: '14px' }}>
                              <div style={{
                                fontSize: '10px',
                                fontWeight: '500',
                                color: 'var(--color-text-tertiary)',
                                marginBottom: '6px',
                              }}>
                                {group.manifestName} · Cerrado{' '}
                                {group.closedAt
                                  ? new Date(group.closedAt).toLocaleDateString('es-SV')
                                  : '—'}
                              </div>
                              {group.packages.map(pkg => (
                                <div
                                  key={pkg.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '8px 0',
                                    borderBottom: '0.5px solid var(--color-border-tertiary)',
                                    fontSize: '11px',
                                  }}
                                >
                                  <span style={{
                                    fontWeight: '500',
                                    color: 'var(--color-text-secondary)',
                                    flexShrink: 0,
                                  }}>
                                    {pkg.unique_package_id || pkg.id?.slice(0, 8)}
                                    {pkg.recipient_name && (
                                      <span style={{
                                        fontSize: '10px',
                                        color: 'var(--color-text-secondary)',
                                        display: 'block',
                                        fontWeight: '400',
                                      }}>
                                        {pkg.recipient_name}
                                      </span>
                                    )}
                                    {pkg.tracking_number && (
                                      <span style={{
                                        fontSize: '9px',
                                        fontFamily: 'monospace',
                                        color: 'var(--color-text-tertiary)',
                                        display: 'block',
                                      }}>
                                        {pkg.tracking_number}
                                      </span>
                                    )}
                                  </span>
                                  <span style={{
                                    flex: 1,
                                    fontFamily: 'var(--font-mono)',
                                    fontSize: '10px',
                                    color: 'var(--color-text-tertiary)',
                                  }}>
                                    Tracking: {pkg.tracking_number || '—'}
                                  </span>
                                  <span style={{
                                    fontSize: '9px',
                                    fontWeight: '600',
                                    padding: '2px 7px',
                                    borderRadius: '8px',
                                    background: '#f0fdf4',
                                    color: '#16a34a',
                                  }}>
                                    Enviado
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* ── SECTION B: Encomiendas ── */}
            {encomiendas.length > 0 && (
              <section className="space-y-3">
                <h2 className="metric-label flex items-center gap-2">
                  <Truck size={13} /> {wt.encomiendaTitle}
                </h2>
                <div className="space-y-4">
                  {encomiendas.map(enc => (
                    <EncomiendaCard
                      key={enc.id}
                      enc={enc}
                      wt={wt}
                      onUploaded={fetchClientData}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
